// ════════════════════════════════════════════════════════════════════════
//  Der Test – einmal vor dem Lehrgang, einmal danach
//
//  Dasselbe Stück Text, zweimal getippt. Daraus werden Wörter pro Minute, und
//  daran lässt sich zeigen, dass der Lehrgang etwas bewirkt hat.
//
//  Es ist EIN Text für beide Messungen: zwei „gleichwertige“ Texte müsste
//  jemand fachlich verantworten, und ohne diese Zusage wäre der Vergleich
//  wertlos. Dass ein Kind den Text beim zweiten Mal wiedererkennt, ist bekannt
//  und steht in der Auswertung dabei.
// ════════════════════════════════════════════════════════════════════════

import { useDraft } from "../../../shared/context/DraftContext.js";
import { NoDraft } from "../../draft/components/NoDraft.js";
import { useAnchorFocus } from "../../../shared/hooks/useAnchorFocus.js";
import { ProblemList } from "../../validation/components/ProblemList.js";
import { problemsAt } from "../../validation/lib/validation.js";
import { anchors } from "../../validation/lib/problemTarget.js";
import { newAssessmentKey } from "../../../shared/lib/contentKeys.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./settings.module.css";

export function Assessment() {
  const { bundle, setBundle, problems, readOnly } = useDraft();
  useAnchorFocus();
  if (!bundle) return <NoDraft />;

  const test = bundle.assessment;

  const change = (part: Partial<NonNullable<typeof test>>) => {
    if (!test) return;
    setBundle({ ...bundle, assessment: { ...test, ...part } });
  };

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>Test</h1>
      <p className={styles.subtitle}>
        Ein kurzer Text, den jedes Kind zweimal tippt: vor dem Lehrgang und nach dem
        letzten Übungssatz. Gemessen wird die Zeit, angezeigt werden Wörter pro Minute.
        Ohne Test läuft der Lehrgang wie bisher – die Kinder merken nichts davon.
      </p>
      <p className={styles.subtitle}>
        Ob der Test stattfindet, entscheidet die Lehrkraft in ihrer Klasse. Hier steht,
        was sie einschalten kann.
      </p>

      <div id={anchors.assessment} className={styles.spaced}>
        <ProblemList problems={problemsAt(problems, "assessment")} />

        {!test && (
          <>
            <p className={styles.empty}>Dieser Lehrgang arbeitet ohne Test.</p>
            <button
              type="button"
              className={styles.button}
              disabled={readOnly}
              onClick={() =>
                setBundle({
                  ...bundle,
                  assessment: {
                    key: newAssessmentKey(bundle),
                    title: "Wie schnell tippst du?",
                    intro:
                      "Tippe die Zeile ab. Lass dir Zeit – wir schauen nur, wie weit du schon bist.",
                    text: "",
                  },
                })
              }
            >
              Test anlegen
            </button>
          </>
        )}

        {test && (
          <>
            <label className={own.limit}>
              Überschrift
              <input
                className={styles.field}
                value={test.title}
                readOnly={readOnly}
                onChange={(e) => change({ title: e.target.value })}
              />
            </label>

            <label>
              Was das Kind vorher liest
              <textarea
                className={styles.field}
                rows={2}
                value={test.intro}
                readOnly={readOnly}
                onChange={(e) => change({ intro: e.target.value })}
              />
            </label>

            <label>
              Testtext
              <textarea
                className={styles.field}
                rows={3}
                value={test.text}
                readOnly={readOnly}
                onChange={(e) => change({ text: e.target.value })}
              />
            </label>
            <p className={own.note}>
              {[...test.text].length} Zeichen. 60 bis 120 sind gut: ein Kind, das die
              Tasten noch sucht, schafft etwa 25 in der Minute.{" "}
              <strong>Keine Großbuchstaben</strong> – die Umschalttaste kommt erst in
              Kapitel 5, vorher wäre die Zeile nicht zu schaffen.
            </p>

            {!readOnly && (
              <button
                type="button"
                className={styles.buttonQuiet}
                onClick={() => {
                  const { assessment: _weg, ...ohne } = bundle;
                  setBundle(ohne);
                }}
              >
                Test entfernen
              </button>
            )}
            <p className={own.note}>
              Entfernen geht nur, solange der Test noch nie veröffentlicht war. An seinem
              Schlüssel hängen die Messungen der Kinder; die Prüfung vor dem
              Veröffentlichen sagt es, wenn er gebraucht wird.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
